import React from 'react'
import { useCurrentFrame, useVideoConfig } from 'remotion'
import { COLORS, FONTS, GLOW, GLASS } from '../shared/theme'
import { fadeIn, entrance, staggerDelay, pulse, float, SPRINGS } from '../shared/animations'
import SceneWrapper from '../shared/SceneWrapper'

/**
 * ComparisonScene — Comparacion lado a lado.
 *
 * params: { left: {label, items}, right: {label, items}, style }
 */
export default function ComparisonScene({ params = {}, artDirection = {} }) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const {
    left = { label: 'Antes', items: [] },
    right = { label: 'Despues', items: [] },
    style = 'versus',
  } = params

  const dividerOpacity = fadeIn(frame, 10, 20)
  const dividerScale = entrance(frame, fps, 10, SPRINGS.snappy)
  const glowPulse = pulse(frame, 0.04, 0.5, 1.0)

  const leftColors = {
    versus: { accent: COLORS.accent.red, label: COLORS.accent.red },
    before_after: { accent: COLORS.text.muted, label: COLORS.text.muted },
    pros_cons: { accent: COLORS.accent.green, label: COLORS.accent.green },
  }
  const rightColors = {
    versus: { accent: COLORS.accent.blue, label: COLORS.accent.blue },
    before_after: { accent: COLORS.accent.blue, label: COLORS.accent.blue },
    pros_cons: { accent: COLORS.accent.red, label: COLORS.accent.red },
  }

  const lc = leftColors[style] || leftColors.versus
  const rc = rightColors[style] || rightColors.versus
  const vs = style === 'versus'

  return (
    <SceneWrapper accent={COLORS.accent.purple} texture={artDirection?.texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Panel izquierdo */}
        <ComparisonPanel
          side={left}
          colors={lc}
          frame={frame}
          fps={fps}
          delay={0}
          slideFrom="left"
        />

        {/* Divisor central con glow */}
        <div
          style={{
            position: 'absolute',
            top: '10%',
            height: '80%',
            width: 2,
            background: `linear-gradient(to bottom, transparent, ${COLORS.text.secondary}60, transparent)`,
            opacity: dividerOpacity,
            transform: `scaleY(${dividerScale})`,
            transformOrigin: 'center',
            zIndex: 10,
            boxShadow: `0 0 12px ${COLORS.accent.purple}30, 0 0 24px ${COLORS.accent.purple}15`,
          }}
        />

        {/* Etiqueta VS */}
        {vs && (
          <div
            style={{
              position: 'absolute',
              zIndex: 11,
              width: 64,
              height: 64,
              borderRadius: '50%',
              ...GLASS.card,
              border: `2px solid ${COLORS.accent.purple}40`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: FONTS.heading,
              fontSize: 18,
              fontWeight: 800,
              color: COLORS.text.primary,
              opacity: dividerOpacity,
              boxShadow: GLOW.md(COLORS.accent.purple),
            }}
          >
            VS
          </div>
        )}

        {/* Panel derecho */}
        <ComparisonPanel
          side={right}
          colors={rc}
          frame={frame}
          fps={fps}
          delay={5}
          slideFrom="right"
        />
      </div>
    </SceneWrapper>
  )
}

function ComparisonPanel({ side, colors, frame, fps, delay, slideFrom }) {
  const slideX = entrance(frame, fps, delay, SPRINGS.gentle)
  const startX = slideFrom === 'left' ? -60 : 60
  const translateX = startX * (1 - slideX)
  const opacity = fadeIn(frame, delay, 20)

  return (
    <div
      style={{
        flex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 80px',
        gap: 32,
        transform: `translateX(${translateX}px)`,
        opacity,
      }}
    >
      {/* Label del panel */}
      <div
        style={{
          fontFamily: FONTS.heading,
          fontSize: 36,
          fontWeight: 800,
          color: colors.accent,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          borderBottom: `2px solid ${colors.accent}40`,
          paddingBottom: 16,
          width: '100%',
          textAlign: 'center',
          textShadow: `0 0 16px ${colors.accent}30`,
        }}
      >
        {side.label}
      </div>

      {/* Items */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          width: '100%',
        }}
      >
        {(side.items || []).slice(0, 4).map((item, i) => {
          const iDelay = staggerDelay(i, 6) + delay + 15
          const iOpacity = fadeIn(frame, iDelay, 12)
          const itemFloat = float(frame, 0.018 + i * 0.005, 2)

          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                opacity: iOpacity,
                transform: `translateY(${itemFloat}px)`,
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: colors.accent,
                  flexShrink: 0,
                  boxShadow: GLOW.sm(colors.accent),
                }}
              />
              <span
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 22,
                  color: COLORS.text.secondary,
                  lineHeight: 1.4,
                }}
              >
                {item}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
